import React, { useState } from 'react';
import { requestNotificationPermission, getTopicForTeam, subscribeToTopic, unsubscribeFromTopic } from '../firebase';

interface NotificationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  teamId?: string;
  teamName: string;
  isClubNotification?: boolean;
}

export const NotificationDialog: React.FC<NotificationDialogProps> = ({
  isOpen,
  onClose,
  teamId,
  teamName,
  isClubNotification = false
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleEnable = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await requestNotificationPermission();

      if (!token) {
        setError('Benachrichtigungen wurden nicht erlaubt');
        setLoading(false);
        return;
      }

      const topic = isClubNotification
        ? 'clubMatchesResults'
        : getTopicForTeam(teamId!);

      const success = await subscribeToTopic(token, topic);

      if (!success) {
        setError('Fehler beim Speichern der Subscription');
        setLoading(false);
        return;
      }

      localStorage.setItem(
        isClubNotification ? 'notifications_club' : `notifications_${teamId}`,
        JSON.stringify({ enabled: true, topic, token })
      );

      setLoading(false);
      onClose();
    } catch (err: any) {
      console.error('Error enabling notifications:', err);
      setError(`Fehler: ${err.message || 'Unbekannter Fehler'}`);
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    setError(null);

    try {
      // Hole gespeicherte Daten
      const storedData = localStorage.getItem(
        isClubNotification ? 'notifications_club' : `notifications_${teamId}`
      );

      if (storedData) {
        const { token, topic } = JSON.parse(storedData);

        if (token && topic) {
          console.log(`Unsubscribing from topic: ${topic}`);

          // Unsubscribe from topic (via Firestore, backend will process)
          await unsubscribeFromTopic(token, topic);
        }
      }

      // Lösche Status lokal
      localStorage.removeItem(
        isClubNotification ? 'notifications_club' : `notifications_${teamId}`
      );

      setLoading(false);
      onClose();
    } catch (err) {
      console.error('Error disabling notifications:', err);
      setError('Fehler beim Deaktivieren der Benachrichtigungen');
      setLoading(false);
    }
  };

  const isEnabled = (() => {
    const stored = localStorage.getItem(
      isClubNotification ? 'notifications_club' : `notifications_${teamId}`
    );
    if (!stored) return false;
    try {
      const data = JSON.parse(stored);
      return data.enabled === true;
    } catch {
      return stored === 'enabled'; // Backwards compatibility
    }
  })();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <svg
              className="w-12 h-12 text-fortuna-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {isEnabled ? 'Benachrichtigungen verwalten' : 'Benachrichtigungen aktivieren'}
            </h3>
            {isEnabled ? (
              <p className="text-gray-600 text-sm mb-4">
                Du erhältst Benachrichtigungen für <span className="font-medium">{teamName}</span>.
              </p>
            ) : (
              <div className="text-gray-600 text-sm mb-4">
                <p>
                  Erhalte Push-Benachrichtigungen zu <span className="font-medium">{teamName}</span>:
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Live-Spiele</li>
                  <li>Spielergebnisse</li>
                  <li>Wichtige Updates</li>
                </ul>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {error}
              </div>
            )}

            {!isEnabled && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-blue-700 text-xs">
                <strong>Hinweis für iOS:</strong> Füge die App zum Home-Bildschirm hinzu, um Benachrichtigungen zu erhalten.
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          {isEnabled ? (
            <>
              <button
                onClick={handleDisable}
                disabled={loading}
                className="flex-1 bg-red-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Wird deaktiviert...' : 'Deaktivieren'}
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-3 rounded-lg font-medium hover:bg-gray-300"
              >
                Schließen
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleEnable}
                disabled={loading}
                className="flex-1 bg-fortuna-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-fortuna-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Aktiviere...' : 'Aktivieren'}
              </button>
              <button
                onClick={onClose}
                disabled={loading}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-3 rounded-lg font-medium hover:bg-gray-300 disabled:opacity-50"
              >
                Abbrechen
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
